import type { NegotiationReview } from '@shared/domain/review';
import { Button, Checkbox, FormControlLabel, FormGroup, FormHelperText } from '@mui/material';
import React, { useState } from 'react';
import { ReviewStep } from '@shared/domain/review';
import { NEGOTIATION_FORM_FIELDS } from '../../data';
import { getReviewStepName } from '../../helpers/ReviewStepNames';
import { useReviewForm } from '../../hooks/useReviewForm';
import { QuestionRenderer } from '../QuestionRenderer';
import '../../styles/review-form.scss';

interface NegotiationReviewProps {
  submitReviewForm?: (
    formFields: NegotiationReview,
    reviewStep: ReviewStep,
    isDraft?: boolean,
  ) => void;
  rfp?: boolean;
  editMode?: boolean;
  defaultValues?: Partial<NegotiationReview> & { id?: number };
  handleEditSuccess?: () => void;
  seedLegalCheckFlags?: Record<string, import('../../legal-check/types').LegalCheckFlag[]>;
}

function NegotiationReviewForm(props: NegotiationReviewProps) {
  const { submitReviewForm, rfp, editMode, defaultValues, handleEditSuccess, seedLegalCheckFlags } = props;
  const [termsAgreed, setTermsAgreed] = useState(false);

  const {
    formFields,
    extendFormFields,
    setFormFields,
    errors,
    validateField,
    handleFormFieldChange,
    handleSubmit,
    handleEdit,
    getLegalCheckBindings,
  } = useReviewForm<NegotiationReview>({
    reviewStep: ReviewStep.NEGOTIATION,
    formFields: NEGOTIATION_FORM_FIELDS,
    editMode,
    defaultValues,
    onSubmit: submitReviewForm,
    onEditSuccess: handleEditSuccess,
    customValidationFields: (!rfp && !editMode) ? { termsAgreed } : {},
    seedLegalCheckFlags,
  });

  const buttonText = rfp ? 'Continue onto RFP' : 'Submit Deal Retro';

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setTermsAgreed(checked);
    validateField('termsAgreed', checked);
  };

  return (
    <div className="review-page">
      <div data-testid="negotiation-review-step-content-anchor" />

      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 6 · Negotiation</p>
        <h1 className="review-page__title">
          {editMode ? 'Edit the negotiation' : 'How did it land?'}
        </h1>
        <p className="review-page__subtitle">
          Notice terms, payment cadence, surprises in procurement. Share the
          moves you'd repeat — and the ones you wouldn't.
        </p>
      </header>

      <form>
        <section className="review-section">
          <QuestionRenderer
            questions={NEGOTIATION_FORM_FIELDS}
            formFields={formFields}
            errors={errors}
            handleFormFieldChange={handleFormFieldChange}
            extendFormFields={extendFormFields}
            validateField={validateField}
            setFormFields={setFormFields}
            getLegalCheckBindings={getLegalCheckBindings}
          />
        </section>

        {!editMode && !rfp && (
          <div className="review-terms" data-error-name="termsAgreed">
            <p>
              By submitting this deal retro, you agree to our
              {' '}
              <a href="https://www.kandir.io/terms">terms and conditions</a>
              .
            </p>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    data-testid="terms-agree-checkbox"
                    checked={termsAgreed}
                    onChange={handleTermsChange}
                  />
                )}
                label="I Agree"
              />
              {errors.termsAgreed && (
                <FormHelperText error>{errors.termsAgreed}</FormHelperText>
              )}
            </FormGroup>
          </div>
        )}
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          onClick={editMode ? handleEdit : handleSubmit}
        >
          {editMode
            ? `Save ${getReviewStepName(ReviewStep.NEGOTIATION)} changes`
            : buttonText}
        </Button>
      </div>
    </div>
  );
}

export default NegotiationReviewForm;
